import { useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useDispatch, useSelector, type TypedUseSelectorHook } from 'react-redux';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';
import { getAbTestV2, trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2.ts';
import headerReducer from '../reducers/headerReducer.ts';
import { actionCreators } from '../actions/index.ts';
import { type useImageSearch_user$key } from './__generated__/useImageSearch_user.graphql.ts';

const TEST_NAME = 'image_search';

const userFragment = graphql`
    fragment useImageSearch_user on User {
        serviceId
    }
`;

const useImageSearchSelector: TypedUseSelectorHook<{ header: ReturnType<typeof headerReducer> }> =
    useSelector;
export const useImageSearch = ({
    user: userRef,
}: {
    user: useImageSearch_user$key | null | undefined;
}): {
    isImageSearchModalOpen: boolean;
    openImageSearchModal?: () => void;
    closeImageSearchModal?: () => void;
} => {
    const user = useFragment(userFragment, userRef);
    const isClient = useClientState();
    const buyerId = user?.serviceId;
    const isEligible = isClient && !!buyerId;
    const dispatch = useDispatch();
    const isImageSearchModalOpen = useImageSearchSelector(
        state => !!state.header.isImageSearchModalOpen
    );
    const isImageSearchAbTestTracked = useImageSearchSelector(
        state => !!state.header.isImageSearchAbTestTracked
    );

    useEffect(() => {
        if (isEligible && !isImageSearchAbTestTracked) {
            trackAbTestV2Variant(TEST_NAME);
            dispatch(actionCreators.setImageSearchAbTestTracked());
        }
    }, [isEligible, dispatch, isImageSearchAbTestTracked]);

    const isVariant = isEligible && getAbTestV2(TEST_NAME)?.variant === 'variant';
    if (!isVariant) {
        return {
            isImageSearchModalOpen: false,
        };
    }

    return {
        isImageSearchModalOpen,
        openImageSearchModal: () => {
            dispatch(actionCreators.setImageSearchModalOpen());
        },
        closeImageSearchModal: () => {
            dispatch(actionCreators.setImageSearchModalClosed());
        },
    };
};
